import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { useNotificationStore } from '../../../store/storeNotifications';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useVectorModal } from '../../../hooks/useVectorMemoryModal';

import {
  Box,
  Button,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Plus } from '@phosphor-icons/react';

import {
  type VectorAddDimensionsRequest,
  VectorService,
} from '../../../service/VectorService';
import { vectorQueries } from '../../../queries/vector';
import { spacesQueries } from '../../../queries/spaces';

const dimensionSchema = z.object({
  title: z.string().min(1, { message: 'Name is required' }),
  description: z.string().min(1, { message: 'Description is required' }),
  isDefault: z.boolean(),
});

type DimensionData = z.infer<typeof dimensionSchema>;

function DimensionSettingsAddDimensionForm() {
  const queryClient = useQueryClient();

  const setNotification = useNotificationStore(state => state.setNotification);

  const {
    name,
    namespaceId,
    spaceId,
    embeddingModel,

    namespaceDimensions,
    onNamespaceDimensionsChange,

    isEditing,
  } = useVectorModal();

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      title: '',
      description: '',
      isDefault: false,
    },
    resolver: zodResolver(dimensionSchema),
  });

  const handleDimensionCreate = (data: DimensionData) => {
    reset();

    if (isEditing) {
      addDimensionsFn({
        body: {
          dimensions: [data],
          embeddingModelName: embeddingModel,
          filesIds: [],
        },
        params: { nameSpace: name, id: namespaceId, spaceId },
      });

      return;
    }

    const newDimensionList = [
      ...namespaceDimensions,
      { ...data, id: crypto.randomUUID() },
    ];

    onNamespaceDimensionsChange(newDimensionList);
  };

  const { mutate: addDimensionsFn } = useMutation({
    mutationFn: ({ body, params }: VectorAddDimensionsRequest) =>
      VectorService.addDimensions({ body, params }),
    onMutate: variables => {
      setNotification({
        type: 'warning',
        message: `Adding dimension ${variables.body.dimensions[0].title}`,
      });
    },
    onError: (_, variables) => {
      setNotification({
        type: 'error',
        message: `Failed to add dimension ${variables.body.dimensions[0].title}`,
      });
    },
    onSuccess: (_, variables) => {
      if (spaceId) {
        return queryClient.invalidateQueries({
          queryKey: spacesQueries.all().queryKey,
        });
      }

      setNotification({
        type: 'success',
        message: `Successfully added dimension ${variables.body.dimensions[0].title}`,
      });

      return queryClient.invalidateQueries({
        queryKey: vectorQueries.allNamespaces().queryKey,
      });
    },
  });

  return (
    <Box
      component="form"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
      }}
      onSubmit={handleSubmit(handleDimensionCreate)}
    >
      <TextField
        placeholder="Type dimension name"
        sx={{ flex: 1 }}
        error={!!errors.title}
        helperText={errors.title?.message}
        {...register('title')}
      />

      <TextField
        placeholder="Type dimension description"
        sx={{ flex: 1 }}
        error={!!errors.description}
        helperText={errors.description?.message}
        {...register('description')}
      />

      <Controller
        control={control}
        name="isDefault"
        render={({ field: { value, onChange } }) => (
          <Stack direction="row" alignItems="center">
            <Switch checked={value} onChange={onChange} />
            <Typography>Assign as default</Typography>
          </Stack>
        )}
      />

      <Button type="submit" variant="outlined">
        Add
        <Plus size={16} />
      </Button>
    </Box>
  );
}

export { DimensionSettingsAddDimensionForm, dimensionSchema };
